import React, { useState, useEffect } from 'react';
import { Table, Modal, Spin, Form, Input, Select, Button, DatePicker } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { updateTask, deleteTask, createComment } from "../../../../../../API/gaps";
import { getTaskStatus, createTaskComment } from "../../../../../../API/tasks";
import Columns from '../TasksTable/columns';
import dayjs from 'dayjs';

import { useAuth } from "../../../../../Context/AuthContext";

const { TextArea } = Input;

const ViewTasks = ({
  visible,
  onCancel,
  selectedIssue,
  loading,
  api,
  tasks = [],
  setTasks,
  issueAreas,
  canGapsUpdate
}) => {
  // Permisos
  const { hasPermission, userData } = useAuth();
  const isSuperuser = userData?.is_superuser === true;
  const hasAnyRole = userData?.roles?.length > 0;

  const [form] = Form.useForm();
  const [editingTask, setEditingTask] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [taskStatus, setTaskStatus] = useState([]);
  const selectedStatusId = Form.useWatch('status', form);

  useEffect(() => {
    const finishedStatus = taskStatus.find(s => s.index === 3);
    
    if (selectedStatusId === finishedStatus?.id) {
      form.setFieldsValue({ 
        date_finished: dayjs()
      });
      
      // ✅ NUEVO: Auto-llenar fecha comprometida si está vacía
      const currentDateCommitted = form.getFieldValue('date_committed');
      if (!currentDateCommitted) {
        form.setFieldsValue({
          date_committed: dayjs()
        });
      }
    } else {
      form.setFieldsValue({ date_finished: null });
    }
  }, [selectedStatusId, form, taskStatus]);

  useEffect(() => {
    if (visible) {
      const fetchTaskStatus = async () => {
        try {
          const response = await getTaskStatus();
          setTaskStatus(response || []);
        } catch (error) {
          console.error("Error cargando estados de tareas", error);
        }
      };
      fetchTaskStatus();
    }
  }, [visible]);

  const handleEditTask = (task) => {
    setEditingTask(task);
    form.setFieldsValue({
      code: task.code,
      name: task.name,
      description: task.description,
      status: task.status?.id,  // ✅ Usar ID del status
      responsible_area_id: task.responsible_area?.id,
      date_finished: task.date_finished ? dayjs(task.date_finished) : null,
      date_committed: task.date_committed ? dayjs(task.date_committed) : null,
    });

    const formattedComments = (task.comments || []).map(comment => ({
      id: comment.id,
      text: comment.comment,
      date: comment.created_at || new Date().toISOString(),
      user: comment.user ? `${comment.user.first_name} ${comment.user.last_name}` : 'Sistema',
      username: comment.user?.username || 'unknown',
      created: comment.created
    }));

    setComments(formattedComments);
  };

  // ✅ Verificar si la tarea está finalizada
  const isTaskFinished = editingTask?.status?.index === 3;

  const handleCancelEdit = () => {
    setEditingTask(null);
    form.resetFields();
  };

  /* const handleUpdateTask = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      
      const response = await updateTask(editingTask.code, {
        issue_code: selectedIssue.code,
        name: values.name,
        description: values.description,
        status_id: values.status,  // ✅ Envía el ID al backend
        responsible_area_id: values.responsible_area_id,
        date_committed: values.date_committed?.toISOString() || null,
        date_finished: values.date_finished?.toISOString() || null
      });

      if (!response) {
        throw new Error('La tarea no pudo ser actualizada');
      }

      setTasks(prev => prev.map(task => 
        task.code === editingTask.code ? { 
          ...task,
          name: values.name,
          description: values.description,
          status: { 
            id: values.status, 
            name: taskStatus.find(s => s.id === values.status)?.name || '' 
          },
          responsible_area: { 
            id: values.responsible_area_id,
            name: issueAreas.find(a => a.id === values.responsible_area_id)?.name || ''
          },
          date_committed: values.date_committed?.toISOString() || null,
          date_finished: values.date_finished?.toISOString() || null
        } : task
      ));

      api.success({ message: 'Tarea actualizada correctamente' });
      handleCancelEdit();
      setTimeout(() => {
        window.location.reload();
      }, 600);
    } catch (error) {
      api.error({ message: 'Error al actualizar la tarea' });
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  }; */

  const handleUpdateTask = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();

      const updateData = {
        issue_code: selectedIssue.code,
        name: values.name,
        description: values.description,
        status_id: values.status,
        responsible_area_id: values.responsible_area_id,
        date_committed: values.date_committed?.toISOString() || null,
        date_finished: values.date_finished?.toISOString() || null
      };

      const response = await updateTask(editingTask.code, updateData);

      if (!response) throw new Error('La tarea no pudo ser actualizada');

      if (newComment.trim()) {
        try {
          const commentData = {
            task_code: editingTask.code,
            comment: newComment.trim()
          };

          const commentResponse = await createTaskComment(commentData);

          const newCommentObj = {
            id: commentResponse.id,
            text: commentResponse.comment,
            date: new Date().toISOString(),
            user: `${commentResponse.user.first_name} ${commentResponse.user.last_name}`,
            username: commentResponse.user.username,
          };

          setComments(prev => [...prev, newCommentObj]);
          setNewComment('');
        } catch (error) {
          console.error("Error creando comentario desde Guardar:", error);
          api.error({ message: "Error al guardar el comentario pendiente" });
        }
      }

      setTasks(prev => prev.map(task =>
        task.code === editingTask.code
          ? {
              ...task,
              name: values.name,
              description: values.description,
              status: {
                id: values.status,
                name: taskStatus.find(s => s.id === values.status)?.name || ''
              },
              responsible_area: {
                id: values.responsible_area_id,
                name: issueAreas.find(a => a.id === values.responsible_area_id)?.name || ''
              },
              date_committed: values.date_committed?.toISOString() || null,
              date_finished: values.date_finished?.toISOString() || null
            }
          : task
      ));

      api.success({ message: 'Tarea actualizada correctamente' });
      handleCancelEdit();

      setTimeout(() => window.location.reload(), 600);

    } catch (error) {
      api.error({ message: 'Error al actualizar la tarea' });
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveTask = async (taskId) => {
    try {
      await deleteTask(taskId);
      setTasks(tasks.filter(task => task.id !== taskId));
      api.success({ message: 'Tarea eliminada correctamente' });
      setTimeout(() => {
        window.location.reload();
      }, 600);
    } catch (error) {
      api.error({ message: 'Error al eliminar la tarea' });
      console.error(error);
    }
  };

  const handleAddComment = async () => {
    if (newComment.trim()) {
      try {
        setIsSubmitting(true);
        
        const commentData = {
          task_code: editingTask.code,
          comment: newComment
        };
  
        const response = await createTaskComment(commentData);
        
        const newCommentObj = {
          id: response.id,
          text: response.comment,
          date: new Date().toISOString(),
          user: `${response.user.first_name} ${response.user.last_name}`,
          username: response.user.username,
        };
  
        setComments(prev => [...prev, newCommentObj]);
        setNewComment('');
        api.success({ message: 'Comentario agregado correctamente' });
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } catch (error) {
        api.error({ message: 'Error al agregar el comentario' });
        console.error('Error adding comment:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Modal
      className="dependency-modal view-task-modal"
      title={<>Tareas para {selectedIssue?.type}</>}
      open={visible}
      onCancel={() => {
        if (editingTask) {
          // Si está editando, volver a la tabla
          handleCancelEdit();
        } else {
          // Si no está editando, cerrar el modal
          onCancel();
        }
      }}
      footer={null}
      width={700}
    >
      <Spin className="custom-spin" spinning={loading || isSubmitting}>
        {editingTask ? (
          <div /* style={{ padding: '16px', border: '1px solid #f0f0f0', borderRadius: '4px', marginBottom: '16px' }} */>
            <Form form={form} layout="vertical">
              <Form.Item
                label="Nombre"
                name="name"
                rules={[{ required: true, message: 'El nombre es requerido' }]}
              >
                <Input.TextArea
                  maxLength={50} 
                  showCount
                  style={{ resize: 'none', width: '100%' }} 
                  placeholder="Nombre de la tarea"
                  disabled={isSubmitting || isTaskFinished}
                />
              </Form.Item>
              
              <Form.Item
                label="Descripción"
                name="description"
                rules={[{ required: true, message: 'La descripción es requerida' }]}
              >
                <Input.TextArea 
                  maxLength={300} 
                  showCount 
                  style={{ resize: 'none', width: '100%', height: '90px' }}
                  disabled={isTaskFinished}
                />
              </Form.Item>
          
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item
                  label="Estado"
                  name="status"
                  rules={[{ required: true, message: 'El estado es requerido' }]}
                >
                  <Select disabled={isTaskFinished}>
                    {taskStatus?.map(status => (
                      <Select.Option key={status.index} value={status.id}>
                        {status.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item 
                  label="Responsable"
                  name="responsible_area_id"
                  rules={[{ required: true, message: 'El área es requerida' }]}
                >
                  <Select disabled={isTaskFinished}>
                    {issueAreas?.map(area => (
                      <Select.Option key={area.id} value={area.id}>
                        {area.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item 
                  label="Fecha Comprometida" 
                  name="date_committed"
                  required={(() => {
                    const initiatedStatus = taskStatus.find(s => s.index === 2);
                    return selectedStatusId === initiatedStatus?.id;
                  })()}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const statusId = getFieldValue('status');
                        const initiatedStatus = taskStatus.find(s => s.index === 2);
                        const pendingStatus = taskStatus.find(s => s.index === 1);
                        
                        if (statusId === pendingStatus?.id) {
                          return Promise.resolve();
                        }
                        if (statusId === initiatedStatus?.id && !value) {
                          return Promise.reject(new Error('La fecha comprometida es obligatoria'));
                        }
    
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker 
                    style={{ width: '100%' }} 
                    format="DD/MM/YYYY"
                    inputReadOnly
                    disabled={(() => {
                      const finishedStatus = taskStatus.find(s => s.index === 3);
                      
                      return (
                        isTaskFinished ||
                        selectedStatusId === finishedStatus?.id
                      );
                    })()}
                    disabledDate={(current) => {
                      return current && current < dayjs().startOf('day');
                    }}
                  />
                </Form.Item>

                <Form.Item 
                  label="Fecha Finalización" 
                  name="date_finished"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const statusId = getFieldValue('status');
                        const finishedStatus = taskStatus.find(s => s.index === 3);
                        
                        // ✅ Comparar con el ID del estado "Finalizado"
                        if (statusId === finishedStatus?.id && !value) {
                          return Promise.reject(new Error('La fecha de finalización es requerida'));
                        }
                        
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker 
                    style={{ width: '100%' }} 
                    format="DD/MM/YYYY"
                    inputReadOnly
                    disabled={true}
                    /* disabled={(() => {
                      const finishedStatus = taskStatus.find(s => s.index === 3);
                      // Bloquear si la tarea ya está finalizada o si el estado seleccionado es Finalizado
                      return (
                        isTaskFinished ||
                        selectedStatusId === finishedStatus?.id
                      );
                    })()} */
                    disabledDate={(current) => {
                      // Permitir solo HOY en adelante
                      return current && current < dayjs().startOf('day');
                    }}
                  />
                </Form.Item>
              </div>

              <Form.Item label="Comentarios" style={{ marginBottom: 24 }}>
                <div className="comments-container">
                  <div className={`comments-input-area ${comments.length > 0 ? 'with-border' : ''}`}>
                    <TextArea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Escribe tu comentario (máx. 300 caracteres)"
                      autoSize={{ minRows: 3, maxRows: 3 }}
                      maxLength={300}
                      className="comments-textarea"
                      disabled={isTaskFinished}
                    />
                    <div className="comments-actions">
                      <span className="comments-counter">
                        {newComment.length}/300
                      </span>
                      <Button 
                        type="primary"
                        size="small"
                        onClick={handleAddComment}
                        disabled={!newComment.trim() || isSubmitting || isTaskFinished}
                        loading={isSubmitting}
                        className="comments-button"
                      >
                        Comentar
                      </Button>
                    </div>
                  </div>
                  
                  {comments.length > 0 ? (
                    <div className="comments-list">
                      {comments.map((comment) => (
                        <div key={comment.id} className="comment-item">
                          <div className="comment-header">
                            <span className="comment-user">{comment.user}</span>
                            <span className="comment-date">
                              {dayjs(comment.created).format('DD/MM/YYYY HH:mm')}
                            </span>
                          </div>
                          <div className="comment-text">
                            {comment.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="comments-empty">
                      No hay comentarios todavía
                    </div>
                  )}
                </div>
              </Form.Item>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <Button className="cancel-button" onClick={handleCancelEdit}>
                  Cancelar
                </Button>
                <Button 
                  className="save-type-button"
                  onClick={handleUpdateTask}
                  icon={<SaveOutlined />}
                  loading={isSubmitting}
                  disabled={isTaskFinished}
                >
                  Guardar
                </Button>
              </div>
            </Form>
          </div>
        ) : (
          <Table
            dataSource={tasks}
            columns={Columns({ 
              handleEditTask, 
              handleRemoveTask, 
              issueAreas,
              taskStatus,
              isSuperuser, 
              hasAnyRole, 
              canGapsUpdate
            })}
            rowKey={(task) => task.code}
            pagination={false}
            size="small"
            bordered
            locale={{ emptyText: 'No hay tareas asignadas' }}
          />
        )}
      </Spin>
    </Modal>
  );
};

export default ViewTasks;